import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import SelectInput from "common/selectInput";
import Modal from "common/modal";
import toInteger from "lodash/toInteger";
import parseInt from "lodash/parseInt";
import { loadCutSheetData } from "../../../../store/siteRepo/actionCreator";
import axios from "services/config";
import { showSpinner, hideSpinner } from "store/slices/spinner";
import { Alert } from "react-bootstrap";

const parseMeasurement = (measurement) => {
  const [feet = 0, inches = 0] = (measurement?.replaceAll(`"`, ``)?.split("'") ?? [])?.map((v) => {
    return isNaN(v) || !v ? 0 : parseInt(v);
  });
  return [feet, inches];
};

const AddOrEditRollModal = ({ roll, projectId, setShowAddModal, productsData }) => {
  const dispatch = useDispatch();
  const { sub } = useSelector((state) => state.loggedInUser);
  const { viewSite } = useSelector((state) => state.siteRepo);

  const [prevWidthFt, prevWidthIn] = parseMeasurement(roll?.width);
  const [prevLengthFt, prevLengthIn] = parseMeasurement(roll?.length);

  // TODO: pull product options from redux store and remove prop
  const [product, setProduct] = useState(
    productsData?.length > 0 ? { id: productsData[0].layerCutSheetId, description: productsData[0].id } : null,
  );
  const [index, setIndex] = useState(roll?.index === undefined ? "" : roll.index);
  const [widthFt, setWidthFt] = useState(roll?.width === undefined ? "" : prevWidthFt);
  const [widthIn, setWidthIn] = useState(roll?.width === undefined ? "" : prevWidthIn);
  const [lengthFt, setLengthFt] = useState(roll?.length === undefined ? "" : prevLengthFt);
  const [lengthIn, setLengthIn] = useState(roll?.length === undefined ? "" : prevLengthIn);

  const AddOrEditRoll = async () => {
    setShowAddModal(false);
    dispatch(showSpinner());
    const project = viewSite.projectDetailsList.find((proj) => proj.projectId === projectId);
    const rolls = (project?.cutSheetData ?? [])
      .filter((p) => p.productName === product.description)
      .flatMap((p) => p.rolls)
      .filter((r) => r.rollId !== roll?.rollId);

    const rollLengthIn = rolls.reduce((total, roll) => {
      const [lengthFt, lengthIn] = parseMeasurement(roll?.length);
      return total + lengthFt * 12 + lengthIn;
    }, 0);

    const [lFt, lIn] = parseMeasurement(`${lengthFt}'${lengthIn}"`);
    const totalLengthIn = rollLengthIn + parseInt(lIn) + parseInt(lFt) * 12;
    const newProductLength = `${Math.floor(totalLengthIn / 12)}'${totalLengthIn % 12}`;

    const [wFt, wIn] = parseMeasurement(`${widthFt}'${widthIn}"`);
    await axios.post(`/fc-site-repo/create/cutSheet/roll`, {
      layerCutSheetId: product.id,
      projectId,
      productName: product.description,
      linearLength: `${wFt}'${wIn}"x${newProductLength}"`,
      updatedBy: sub,
      rolls: [{ rollId: "", rollCuts: [], ...roll, index, length: `${lFt}'${lIn}"`, width: `${wFt}'${wIn}"` }],
    });

    // TODO: replace this with a dispatch that modifies store state based on response of previous request
    dispatch(loadCutSheetData(projectId));
    dispatch(hideSpinner());
  };

  return (
    <Modal
      title={roll === undefined ? "Add Roll" : "Edit Roll"}
      handleSave={() => AddOrEditRoll()}
      handleCancel={() => setShowAddModal(false)}
      size="lg"
      sectionName=""
      closeButtonText="Cancel"
      saveButtonText="Save"
      isSaveBtnDisabled={
        (toInteger(lengthFt) === 0 && toInteger(lengthIn) === 0) ||
        (toInteger(widthFt) === 0 && toInteger(widthIn) === 0) ||
        [undefined, "", "-- Select value --"].includes(product.description)
      }
    >
      {((lengthFt === 0 && lengthIn === 0) || (widthFt === 0 && widthIn === 0)) && (
        <Alert variant="danger">{`${lengthFt === 0 && lengthIn === 0 ? "length" : "width"} must be larger than 0`}</Alert>
      )}
      <div className="container-fluid p-2 border-0 m-0">
        <div className="search-form-container__fields">
          <form className="search-form-container__fields__form">
            <div className="row">
              <div className="w-50">
                <SelectInput
                  isPrc={false}
                  options={productsData.map((productKey) => ({
                    id: productKey.layerCutSheetId ?? "",
                    description: productKey.id,
                  }))}
                  label="Product"
                  value={product?.description ?? "Select a Product"}
                  handleSelectInput={(e, value) => setProduct(value)}
                  disabled={productsData?.length === 1}
                />
              </div>
              <label className="search-form-container__fields__form-group d-block w-50">
                Index
                <input
                  className="form-control search-form-container__fields__form-group__item"
                  style={{ margin: "8px 0" }}
                  id="index"
                  type="text"
                  placeholder="Enter Index"
                  value={index}
                  onChange={(e) => setIndex(e.target.value)}
                />
              </label>
              <label className="search-form-container__fields__form-group d-block w-50">
                Length (in feet)
                <input
                  className="form-control search-form-container__fields__form-group__item"
                  style={{ margin: "8px 0" }}
                  id="lengthFeet"
                  type="number"
                  placeholder="Enter Feet"
                  value={lengthFt}
                  onChange={(e) => setLengthFt(e.target.value === "" ? "" : toInteger(e.target.value.substring(0, 4)))}
                  onKeyDown={(e) => new RegExp("[0-9]|Backspace|Delete").test(e.key) || e.preventDefault()}
                />
              </label>
              <label className="search-form-container__fields__form-group d-block w-50">
                Length (in inches)
                <input
                  className="form-control search-form-container__fields__form-group__item"
                  style={{ marginTop: "8px" }}
                  id="lengthInches"
                  type="number"
                  placeholder="Enter Inches"
                  value={lengthIn}
                  onChange={(e) => setLengthIn(e.target.value === "" ? "" : Math.min(toInteger(e.target.value), 11))}
                  onKeyDown={(e) => new RegExp("[0-9]|Backspace|Delete").test(e.key) || e.preventDefault()}
                />
              </label>
              <label className="search-form-container__fields__form-group d-block w-50">
                Width (in feet)
                <input
                  className="form-control search-form-container__fields__form-group__item"
                  style={{ margin: "8px 0" }}
                  id="widthFeet"
                  type="number"
                  placeholder="Enter Feet"
                  value={widthFt}
                  onChange={(e) => setWidthFt(e.target.value === "" ? "" : toInteger(e.target.value.substring(0, 4)))}
                  onKeyDown={(e) => new RegExp("[0-9]|Backspace|Delete").test(e.key) || e.preventDefault()}
                />
              </label>
              <label className="search-form-container__fields__form-group d-block w-50">
                Width (in inches)
                <input
                  className="form-control search-form-container__fields__form-group__item"
                  style={{ marginTop: "8px" }}
                  id="widthInches"
                  type="number"
                  placeholder="Enter Inches"
                  value={widthIn}
                  onChange={(e) => setWidthIn(e.target.value === "" ? "" : Math.min(toInteger(e.target.value), 11))}
                  onKeyDown={(e) => new RegExp("[0-9]|Backspace|Delete").test(e.key) || e.preventDefault()}
                />
              </label>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default AddOrEditRollModal;
